/**
 * ConvertToGroupModal Component
 *
 * Modal for converting a direct (1:1) conversation to a group by adding new members.
 * Shows the current participant as a locked member and allows selecting additional members.
 * Includes an optional group name input and a warning about message history visibility.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  UserPlus,
  Check,
  Loader2,
  Users,
  AlertTriangle,
  Lock,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useConnections, useConnectionActions } from '@/stores/connection';
import { useMessagingActions } from '@/stores/messaging';
import type { UserConnection } from '@/types/connections';
import type { Conversation } from '@/types/messaging';

interface ConvertToGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: Conversation;
}

const MAX_GROUP_SIZE = 50;

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * Get display name from participant
 */
const getParticipantDisplayName = (participant: {
  firstName?: string;
  lastName?: string;
  username: string;
}): string => {
  if (participant.firstName && participant.lastName) {
    return `${participant.firstName} ${participant.lastName}`;
  }
  if (participant.firstName) {
    return participant.firstName;
  }
  return participant.username;
};

/**
 * LockedMemberCard - Shows the existing 1:1 participant as a locked member
 */
interface LockedMemberCardProps {
  participant: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    isOnline?: boolean;
  };
}

const LockedMemberCard: React.FC<LockedMemberCardProps> = ({ participant }) => {
  const displayName = getParticipantDisplayName(participant);
  const initials = getInitials(participant.firstName, participant.lastName, participant.username);

  return (
    <div className="w-full p-3 rounded-lg border border-gray-100 bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar className="h-9 w-9">
            <AvatarImage src={participant.avatar} alt={displayName} />
            <AvatarFallback
              style={{
                backgroundColor: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
              }}
              className="text-xs font-semibold"
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {participant.isOnline && (
            <span
              className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card"
              style={{ backgroundColor: 'hsl(var(--primary))' }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          <p className="text-xs text-gray-500">@{participant.username}</p>
        </div>

        <div className="flex items-center gap-1.5 text-gray-400">
          <Lock className="h-3.5 w-3.5" />
          <span className="text-xs">Current</span>
        </div>
      </div>
    </div>
  );
};

/**
 * SelectableConnectionCard
 */
interface SelectableConnectionCardProps {
  connection: UserConnection;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const SelectableConnectionCard: React.FC<SelectableConnectionCardProps> = ({
  connection,
  isSelected,
  onToggle,
  disabled,
}) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`w-full p-3 rounded-lg border transition-all duration-200 text-left ${
        isSelected
          ? 'border-primary bg-primary/5'
          : disabled
          ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
          : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar className="h-9 w-9">
            <AvatarImage src={connection.user.avatar} alt={displayName} />
            <AvatarFallback
              style={{
                backgroundColor: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
              }}
              className="text-xs font-semibold"
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {connection.user.isOnline && (
            <span
              className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card"
              style={{ backgroundColor: 'hsl(var(--primary))' }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          <p className="text-xs text-gray-500">@{connection.user.username}</p>
        </div>

        <div
          className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'
          }`}
        >
          {isSelected && <Check className="h-3 w-3 text-white" />}
        </div>
      </div>
    </button>
  );
};

export const ConvertToGroupModal: React.FC<ConvertToGroupModalProps> = ({
  open,
  onOpenChange,
  conversation,
}) => {
  const connections = useConnections();
  const { fetchConnections } = useConnectionActions();
  const { convertToGroup } = useMessagingActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the other participant in the 1:1 conversation
  const currentParticipant = conversation.participant;

  // Calculate remaining slots (2 already in 1:1 + new members)
  const currentMemberCount = 2; // Self + other participant
  const remainingSlots = MAX_GROUP_SIZE - currentMemberCount;

  // Filter connections: exclude current participant and self (handled by connections list)
  const availableConnections = useMemo(() => {
    if (!currentParticipant) return connections;
    return connections.filter((c) => c.user.id !== currentParticipant.id);
  }, [connections, currentParticipant]);

  // Filter by search query
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return availableConnections;

    const query = searchQuery.toLowerCase().trim();
    return availableConnections.filter((c) => {
      const name = `${c.user.firstName} ${c.user.lastName}`.toLowerCase();
      const username = c.user.username.toLowerCase();
      return name.includes(query) || username.includes(query);
    });
  }, [availableConnections, searchQuery]);

  // Fetch connections when modal opens
  useEffect(() => {
    if (open) {
      fetchConnections();
    }
  }, [open, fetchConnections]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedUserIds(new Set());
      setGroupName('');
      setError(null);
    }
  }, [open]);

  /**
   * Toggle user selection
   */
  const handleToggleUser = useCallback(
    (userId: string) => {
      setSelectedUserIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(userId)) {
          newSet.delete(userId);
        } else {
          // Check if we can add more
          if (newSet.size >= remainingSlots) {
            return prev;
          }
          newSet.add(userId);
        }
        return newSet;
      });
      setError(null);
    },
    [remainingSlots]
  );

  /**
   * Handle converting to group
   */
  const handleConvert = useCallback(async () => {
    if (selectedUserIds.size === 0) return;

    setIsConverting(true);
    setError(null);

    try {
      const memberIds = Array.from(selectedUserIds);
      const convertedConversation = await convertToGroup(
        conversation.id,
        memberIds,
        groupName.trim() || undefined
      );

      if (convertedConversation) {
        onOpenChange(false);
      }
    } catch (err) {
      console.error('[ConvertToGroupModal] Failed to convert:', err);
      setError(err instanceof Error ? err.message : 'Failed to convert to group');
    } finally {
      setIsConverting(false);
    }
  }, [selectedUserIds, conversation.id, groupName, convertToGroup, onOpenChange]);

  const canConvert = selectedUserIds.size > 0 && !isConverting;
  const isAtMaxCapacity = selectedUserIds.size >= remainingSlots;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="max-w-md max-h-[80vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--primary) / 0.1)' }}
            >
              <Users className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
            </div>
            Create Group
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1 ml-12">
            Add people to start a group conversation
          </DialogDescription>
        </DialogHeader>

        {/* Warning about history visibility */}
        <div className="px-6 pb-4">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-sm text-amber-800">
              New members will only see messages sent after they join.
            </AlertDescription>
          </Alert>
        </div>

        {/* Group name input */}
        <div className="px-6 pb-4">
          <label htmlFor="groupName" className="text-sm font-medium text-gray-700 mb-1.5 block">
            Group name (optional)
          </label>
          <Input
            id="groupName"
            placeholder="Enter a group name..."
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            maxLength={100}
            className="bg-white border-gray-200 focus:border-primary focus:ring-primary/20"
          />
        </div>

        {/* Current member (locked) */}
        {currentParticipant && (
          <div className="px-6 pb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Current conversation</p>
            <LockedMemberCard participant={currentParticipant} />
          </div>
        )}

        {/* Search input */}
        <div className="px-6 pb-2">
          <p className="text-sm font-medium text-gray-700 mb-2">Add people</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-gray-200 focus:border-primary focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Selection info */}
        {selectedUserIds.size > 0 && (
          <div className="px-6 pb-2">
            <p className="text-sm text-gray-600">
              {selectedUserIds.size} selected
              {isAtMaxCapacity && (
                <span className="text-amber-600 ml-2">(max capacity)</span>
              )}
            </p>
          </div>
        )}

        {/* Connections list */}
        <ScrollArea className="flex-1 min-h-[120px] max-h-[180px]">
          <div className="px-6 pb-4 space-y-2">
            {filteredConnections.length === 0 ? (
              <div className="text-center py-6">
                <div
                  className="h-10 w-10 rounded-full mx-auto mb-2 flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                >
                  <Users className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
                </div>
                <p className="text-sm font-medium mb-1 text-gray-900">
                  {searchQuery ? 'No connections found' : 'No connections available'}
                </p>
                <p className="text-xs text-gray-500">
                  {searchQuery ? 'Try a different search term' : 'Connect with more users to add them'}
                </p>
              </div>
            ) : (
              filteredConnections.map((connection) => {
                const isSelected = selectedUserIds.has(connection.user.id);
                const isDisabled = !isSelected && isAtMaxCapacity;
                return (
                  <SelectableConnectionCard
                    key={connection.connectionId}
                    connection={connection}
                    isSelected={isSelected}
                    onToggle={() => handleToggleUser(connection.user.id)}
                    disabled={isDisabled}
                  />
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Error message */}
        {error && (
          <div className="px-6 pb-2">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 pt-4 border-t bg-gray-50 flex justify-between items-center">
          <span className="text-xs text-gray-500">
            {remainingSlots - selectedUserIds.size} slot
            {remainingSlots - selectedUserIds.size !== 1 ? 's' : ''} remaining
          </span>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConvert}
              disabled={!canConvert}
              style={
                canConvert
                  ? {
                      backgroundColor: 'hsl(var(--primary))',
                      color: 'hsl(var(--primary-foreground))',
                    }
                  : undefined
              }
            >
              {isConverting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Group
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToGroupModal;
